
import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SortField, SortOrder, ViewMode } from "@/components/candidates/CandidatesViewOptions";
import { CompanyRelationship, isCompanyRelationship } from "@/utils/companyRelationship";

interface CompaniesFilterContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  assignedToMe: boolean;
  setAssignedToMe: (assigned: boolean) => void;
  showFavoritesOnly: boolean;
  setShowFavoritesOnly: (show: boolean) => void;
  relationshipFilter: CompanyRelationship[];
  setRelationshipFilter: (rels: CompanyRelationship[]) => void;
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortOrder: SortOrder;
  setSortOrder: (order: SortOrder) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  clearAllFilters: () => void;
}

interface CompaniesFilterProviderProps {
  children: ReactNode;
  viewMode?: ViewMode;
  setViewMode?: (mode: ViewMode) => void;
}

const CompaniesFilterContext = createContext<CompaniesFilterContextType | undefined>(undefined);

export function CompaniesFilterProvider({ 
  children, 
  viewMode: externalViewMode, 
  setViewMode: externalSetViewMode 
}: CompaniesFilterProviderProps) {
	const [searchParams, setSearchParams] = useSearchParams();
	
	// Initialize state from URL parameters
	const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
	const [assignedToMe, setAssignedToMe] = useState<boolean>(() => searchParams.get("assignedToMe") === "true");
	const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(() => searchParams.get("favoritesOnly") === "true");
	const [relationshipFilter, setRelationshipFilter] = useState<CompanyRelationship[]>(() => {
		const raw = searchParams.get("relationship");
		if (!raw) return [];
		return raw.split(",").filter(isCompanyRelationship);
	});
	const [sortField, setSortField] = useState<SortField>(() => {
		const field = searchParams.get("sortField");
		return (field as SortField) || "name";
	});
	const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
		const order = searchParams.get("sortOrder");
		return (order as SortOrder) || "asc";
	});
	const [internalViewMode, setInternalViewMode] = useState<ViewMode>("card");
	const [currentPage, setCurrentPage] = useState(() => {
		const page = searchParams.get("page");
		return page ? parseInt(page, 10) : 1;
	});

	// Use either external view mode state or internal
	const viewMode = externalViewMode !== undefined ? externalViewMode : internalViewMode;
	const setViewMode = externalSetViewMode || setInternalViewMode;

	// Update URL parameters whenever filters change
	useEffect(() => {
		const params = new URLSearchParams(searchParams);
		
		// Clear only filter-related params
		params.delete("search");
		params.delete("assignedToMe");
		params.delete("favoritesOnly");
		params.delete("relationship");
		params.delete("sortField");
		params.delete("sortOrder");
		params.delete("page");

		if (searchTerm)
			params.set("search", searchTerm);
		if (assignedToMe)
			params.set("assignedToMe", "true");
		if (showFavoritesOnly)
			params.set("favoritesOnly", "true");
		if (relationshipFilter.length > 0)
			params.set("relationship", relationshipFilter.join(","));
		if (sortField !== "name")
			params.set("sortField", sortField);
		if (sortOrder !== "asc")
			params.set("sortOrder", sortOrder);
		if (currentPage !== 1)
			params.set("page", currentPage.toString());

		setSearchParams(params, { replace: true });
	}, [searchTerm, assignedToMe, showFavoritesOnly, relationshipFilter, sortField, sortOrder, currentPage, searchParams]);

	const clearAllFilters = () => {
		setSearchTerm("");
		setAssignedToMe(false); // Reset to show all companies
		setShowFavoritesOnly(false);
		setRelationshipFilter([]);
		setCurrentPage(1);
	};

  return (
    <CompaniesFilterContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        assignedToMe,
        setAssignedToMe,
        showFavoritesOnly,
        setShowFavoritesOnly,
        relationshipFilter,
        setRelationshipFilter,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        viewMode,
        setViewMode,
        currentPage,
        setCurrentPage,
        clearAllFilters,
      }}
    >
      {children}
    </CompaniesFilterContext.Provider>
  );
}

export const useCompaniesFilter = () => {
  const context = useContext(CompaniesFilterContext);
  if (context === undefined) {
    throw new Error("useCompaniesFilter must be used within a CompaniesFilterProvider");
  }
  return context;
};
